import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getReviewPendingNavEntriesForFacility } from "../../../constants/reviewPendingNavConfig";
import { UserGeneralInfoContextKeys } from "../../../enums/userGeneralInfoContextKeys";
import { RootState } from "../../../redux/store/store";
import { UserStore } from "../../../store/UserGeneralInfoStore";
import { capitalizeWordsSeperatedByUnderScore } from "../../../utils/stringModifications";
import { swapUnderscoreHyphen } from "../../../utils/swapUnderscoreWithHypen";
import ReviewPendingOrdersTableFromAlgolia from "./components/reviewPendingOrdersTableFromAlgolia";

/**
 * Layout component for the Review Pending Orders section. Reads the authStatus
 * URL param to select the active tab, redirects to a valid tab when the segment
 * is missing or invalid, and renders the table with the appropriate header.
 */
const ReviewPendingOrdersLayout = () => {
  const { authStatus } = useParams<{ authStatus?: string }>();
  const navigate = useNavigate();
  const { data: singleHealthFacilityDetails } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );

  const allowedEntries = getReviewPendingNavEntriesForFacility(
    singleHealthFacilityDetails,
  );
  const currentId = authStatus
    ? `review-pending/${authStatus}`
    : allowedEntries[0]?.id;

  useEffect(() => {
    if (allowedEntries.length === 0) {
      navigate("/worklists/medical-pa-orders", { replace: true });
    } else if (!authStatus || !allowedEntries.some((e) => e.id === currentId)) {
      navigate(`/worklists/${allowedEntries[0].id}`, { replace: true });
    }
  }, [allowedEntries, authStatus, currentId, navigate]);

  useEffect(() => {
    const pathAfterWorklists = window.location.pathname.replace(
      /^\/worklists\/?/,
      "",
    );
    UserStore.setItem(
      UserGeneralInfoContextKeys.PARENT_TABLE,
      pathAfterWorklists || "review-pending",
    );
  }, [authStatus]);

  const currentEntry = allowedEntries.find((e) => e.id === currentId);
  /** When URL has no segment yet, pass first tab's segment so table filter uses config default, not hardcoded no_auth_required. */
  const effectiveAuthStatus =
    authStatus ?? allowedEntries[0]?.id?.replace(/^review-pending\//, "");
  const header = currentEntry
    ? `${currentEntry.label} Orders`
    : authStatus
      ? `${capitalizeWordsSeperatedByUnderScore(swapUnderscoreHyphen(authStatus))} Review Pending Orders`
      : "Review Pending Orders";

  return (
    <div className="review-pending-orders--main-container flex h-full flex-col overflow-hidden">
      <div className="review-pending-orders__header mb-4 text-h11 font-bold text-primaryGray-1">
        {header}
      </div>
      <div className="review-pending-orders__table-container flex-1 overflow-auto">
        <ReviewPendingOrdersTableFromAlgolia authStatus={effectiveAuthStatus} />
      </div>
    </div>
  );
};

export default ReviewPendingOrdersLayout;
